import type { CardfinderSessionType } from '@/db/tables';
import type { LocalFunnelSession } from '../../storage';

export type ResolveCompleteActionInput = {
  /** Whether the answers describe a run the user actually finished. */
  isRunComplete: boolean;
  localSession: LocalFunnelSession | undefined;
  /** Derived from the auth cookie, not the identity query: this only needs to be
   *  stable enough to decide whether a re-save is worth attempting. */
  sessionType: CardfinderSessionType;
  isPersistenceEnabled: boolean;
};

export type CompleteAction =
  /** Nothing to write: unfinished, already committed, recovered, or persistence off. */
  | { kind: 'skip' }
  /** Commit the run. `id` is null when one still has to be minted. */
  | { kind: 'complete-run'; id: string | null; attemptedAs: CardfinderSessionType };

/**
 * Pure policy for committing a funnel run. Derived entirely from durable state,
 * so committing the same run twice is a no-op.
 */
export function resolveCompleteAction({
  isRunComplete,
  localSession,
  sessionType,
  isPersistenceEnabled,
}: ResolveCompleteActionInput): CompleteAction {
  // A finished run is permanent: its answers can't change later (editing starts a new run).
  // So we only complete a run the user actually finished
  if (!isRunComplete) return { kind: 'skip' };

  if (!isPersistenceEnabled) return { kind: 'skip' };

  if (localSession?.kind === 'complete') {
    // Finished anonymously: saved as (session_id, ajs_anonymous_id). Once logged in,
    // we save again as a new row (same session_id, account identity) so they can read it back.
    return isAnonymousCompleteNowAuthenticated(localSession, sessionType === 'authenticated')
      ? { kind: 'complete-run', id: localSession.id, attemptedAs: 'authenticated' }
      : { kind: 'skip' };
  }

  // A recovered run has no write target: those answers already are a completed
  // session belonging to this caller.
  if (localSession?.kind === 'recovered') return { kind: 'skip' };

  return { kind: 'complete-run', id: localSession?.id ?? null, attemptedAs: sessionType };
}

/**
 * The one case where viewing recommendations may write: a run finished while
 * anonymous, whose owner has since authenticated, needs saving under their real
 * identity to be readable back. Must stay this narrow — a finished run with no
 * local record is not re-committed just because it was viewed.
 */
export function isAnonymousCompleteNowAuthenticated(
  localSession: LocalFunnelSession | undefined,
  hasAuthToken: boolean,
): boolean {
  return (
    hasAuthToken && localSession?.kind === 'complete' && localSession.attemptedAs === 'anonymous'
  );
}
